import { ChangeDetectorRef, Directive, ElementRef, inject } from '@angular/core';
import { LogoTone, LogoToneService } from '../../services/logo-tone/logo-tone.service';

/**
 * Attribute directive — stamps `data-logo-tone` on an <img> describing how
 * bright the loaded artwork is, so SCSS can draw a contrasting plate behind it.
 *
 * Usage:
 *   <img [src]="event_data.logo" stupaLogoTone alt="" class="brand_image" />
 *
 * Reads the element's own `currentSrc` on `load`, so it follows any `[src]`
 * change without a second binding. Tone resolution is delegated to
 * LogoToneService, which caches per URL and never rejects.
 */
@Directive({
  selector: 'img[stupaLogoTone]',
  standalone: true,
  host: {
    '(load)': 'onLoad()',
    '(error)': 'onError()',
  },
})
export class LogoToneDirective {
  private readonly host = inject(ElementRef<HTMLImageElement>);
  private readonly logoTone = inject(LogoToneService);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Guards against a late resolve overwriting a newer image's tone. */
  private currentUrl = '';

  onLoad(): void {
    const element: HTMLImageElement = this.host.nativeElement;
    const url = element.currentSrc || element.src;
    if (!url || url === this.currentUrl) {
      return;
    }

    this.currentUrl = url;
    this.logoTone.resolve(url).then((tone) => {
      if (this.currentUrl === url) {
        this.apply(tone);
      }
    });
  }

  onError(): void {
    this.currentUrl = '';
    this.apply('neutral');
  }

  private apply(tone: LogoTone): void {
    (this.host.nativeElement as HTMLImageElement).dataset['logoTone'] = tone;
    this.cdr.markForCheck();
  }
}
